/**
 * `GET /v1/fees/quote` — what one instruction would cost (#159).
 *
 * # A GET, and that is load-bearing
 *
 * The route's own description: *"It decides nothing, records nothing and moves
 * nothing; collecting a fee is a separate act at the institution. A POST would
 * read to a client author as an instruction, and the one thing this route must
 * never become is a way to charge somebody."* Nothing in this module writes.
 *
 * # The label is the schedule's, never derived
 *
 * `label` is the same string `/v1/fees/schedule` and `/v1/fees/disclosure`
 * render for this item, taken off the same rendering. The contract is explicit
 * about why a client must not build its own from `kind`: it *"would satisfy
 * every test anybody writes and drift from the disclosure the first time an
 * operator republished a list with better wording."*
 *
 * # A 404 is an answer
 *
 * *"The customer's schedule does not price this kind, method and currency. An
 * ANSWER rather than a fault... There is no fallback to another rail's price
 * and no fallback to another currency."* So `not_priced` is its own state here,
 * and nothing downstream may substitute a different quote for it.
 */
import { moneyFromDTO } from '@/lib/money'
import type { Money } from '@/lib/money'
import { ApiError } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export interface FeeQuote {
  /** Rendered verbatim. The schedule's wording, not this app's. */
  readonly label: string
  /** What would be charged. Zero when `waived`, which is a different fact. */
  readonly fee: Money
  /**
   * Whether the schedule waives it.
   *
   * Published separately from a zero fee on purpose: "no fee is charged for
   * you" and "the fee for this is nothing" are different statements to a
   * customer, and only one of them can change when a list is republished.
   */
  readonly waived: boolean
  /** Which published list priced it, so support can resolve a disagreement. */
  readonly listCode: string
  readonly listVersion: number
}

export type QuoteState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly quote: FeeQuote }
  /** The schedule does not price this. NOT a failure, and never substituted. */
  | { readonly status: 'not_priced' }
  | { readonly status: 'unavailable'; readonly reason: string }

interface QuoteDTO {
  label?: unknown
  fee_minor?: unknown
  currency?: unknown
  waived?: unknown
  list_code?: unknown
  list_version?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

export function decodeQuote(dto: QuoteDTO): FeeQuote | string {
  const label = str(dto.label)
  // Not defaulted to the kind, and not to an empty string. A fee with no
  // wording is one nobody can put next to a disclosure.
  if (label === undefined) return 'the fee arrived with no label'

  const currency = str(dto.currency)
  if (currency === undefined) return 'the fee arrived with no currency'

  let fee: Money
  try {
    fee = moneyFromDTO({ amount_minor: dto.fee_minor, currency } as never)
  } catch (err) {
    return `the fee could not be read (${err instanceof Error ? err.message : String(err)})`
  }

  if (typeof dto.waived !== 'boolean') {
    return 'the fee arrived without saying whether it is waived'
  }

  const listCode = str(dto.list_code)
  const listVersion = dto.list_version
  if (listCode === undefined || typeof listVersion !== 'number') {
    return 'the fee arrived without naming the list that priced it'
  }

  return { label, fee, waived: dto.waived, listCode, listVersion }
}

export interface QuoteQuery {
  readonly kind: string
  readonly currency: string
  /**
   * Absent is NOT zero.
   *
   * The contract: "Absent for a fee with no base, which is a different fact
   * from a base of zero." So an empty input asks for the unbased price rather
   * than the price of nothing.
   */
  readonly amountMinor?: bigint
  readonly method?: string
}

export async function fetchQuote(api: WalletApi, q: QuoteQuery): Promise<QuoteState> {
  const params = new URLSearchParams({ kind: q.kind, currency: q.currency })
  // String(bigint), exact at every magnitude — never Number() (CLAUDE.md §3).
  if (q.amountMinor !== undefined) params.set('amount_minor', String(q.amountMinor))
  if (q.method !== undefined) params.set('method', q.method)

  let dto: QuoteDTO
  try {
    dto = await api.get<QuoteDTO>(`/v1/fees/quote?${params.toString()}`)
  } catch (err) {
    // 404 is the schedule saying it does not price this, which is an answer.
    // Anything else is a failure to get one.
    if (err instanceof ApiError && err.status === 404) return { status: 'not_priced' }
    return { status: 'unavailable', reason: err instanceof Error ? err.message : String(err) }
  }

  const decoded = decodeQuote(dto ?? {})
  if (typeof decoded === 'string') return { status: 'unavailable', reason: decoded }
  return { status: 'ready', quote: decoded }
}
